import { P2GAlert, P2GBox, P2GLinkGroup } from '@parcel2go/core-ui';
import { P2GLink } from '@parcel2go/core-ui';
import { P2GText } from '@parcel2go/core-ui';
import { ReactNode } from 'react';
import { SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import { SettingsCache } from '~/lib/consts/settings-cache';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import ItemEstimatedCollectionDate from './ItemEstimatedCollectionDate';
import SelectOrCopy from './SelectOrCopy';
import LiveTrackingProgress from './LiveTrackingProgress';
import { ServiceImage } from './ItemService';
import { TruncateString } from '~/apps/smartsend/lib/helpers/StringHelper';
import { Truncate } from '../Truncate';

interface props {
  children?: ReactNode;
  variant?: 'body2';
  trackingNumber: string;
  item: SmartSendItemApiModel;
  viewTracking: () => void;
  viewActions: () => void;
  viewDetails: () => void;
}

export default function ItemBookedService(props: props) {
  const service = props.item.BookingDetails.Service;
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);

  return (
    <P2GAlert
      type="success"
      icon={<ServiceImage width={60} service={service} />}
      sx={{ '& .MuiAlert-message': { width: '100%' } }}
    >
      <P2GBox
        sx={{
          display: 'flex',
          alignItems: { sm: 'flex-start', lg: 'center' },
          gap: { sm: 2, md: 0, lg: 2 },
          flexDirection: { md: 'column', lg: 'row' }
        }}
      >
        <Truncate>
          <P2GText variant={props.variant}>
            <strong>{TruncateString(service.Name, 30)}</strong>
          </P2GText>
        </Truncate>
        <P2GBox>
          <P2GLinkGroup
            links={[
              <P2GLink key="view-details" variant={props.variant} onClick={props.viewDetails}>
                {t('DETAILS_LINK', { defaultValue: 'Details' })}
              </P2GLink>,
              <P2GLink key="view-actions" variant={props.variant} onClick={props.viewActions}>
                {t('VIEW_ACTIONS_LINK', { defaultValue: 'Actions' })}
              </P2GLink>
            ]}
          />
        </P2GBox>
      </P2GBox>
      <P2GBox
        sx={{
          display: 'flex',
          alignItems: { sm: 'flex-start', lg: 'center' },
          gap: { sm: 2, md: 0, lg: 2 },
          mt: 1,
          flexDirection: { md: 'column', lg: 'row' }
        }}
      >
        {props.item.BookingDetails.Reference && (
          <P2GText variant="caption">
            <strong>{props.item.BookingDetails.Reference}</strong> &nbsp;&nbsp;
            <SelectOrCopy
              key="reference"
              variant="caption"
              code={props.item.BookingDetails.Reference}
              copied="Copied"
            />
          </P2GText>
        )}
        {props.trackingNumber && (
          <P2GText variant="caption" component="p">
            <strong>{props.trackingNumber}</strong> &nbsp;&nbsp;
            <SelectOrCopy variant="caption" code={props.trackingNumber} copied="Copied" />
          </P2GText>
        )}
      </P2GBox>
      {SettingsCache.settings.Features.includes('Tracking') && (
        <P2GBox onClick={props.viewTracking} mt={1} sx={{ minHeight: '10px' }}>
          <LiveTrackingProgress item={props.item} />
        </P2GBox>
      )}
      <ItemEstimatedCollectionDate
        wrapper={(x) => <P2GBox mt={1}>{x}</P2GBox>}
        variant={props.variant}
        bookingDetails={props.item.BookingDetails}
      />
    </P2GAlert>
  );
}
